/**
 * Single source of truth for colour. The same values live in `style.css` as CSS
 * variables; anything that needs a number (three.js materials, canvas labels)
 * reads them from here.
 */

export const PALETTE = {
  bg: '#FBF7F2',
  ink: '#3B3A48',
  peach: '#FFD6C2',
  mint: '#CDEFE1',
  butter: '#FFF2B3',
  lilac: '#E3D7FF',
  sky: '#CFE7FF',
  rose: '#FFD9E6',
  sage: '#D9E8C8',
} as const;

export type PaletteKey = keyof typeof PALETTE;

/** Terrain colours — pastel, one per tile type. */
export const TILE_COLORS = {
  grass: '#CBE3B4',
  dirt: '#E4CDB4',
  sand: '#F3E4C3',
  water: '#BEDDF6',
  stone: '#D9D6DE',
  path: '#EADFCF',
  crop: '#E9DE9E',
  plank: '#E8C79B',
  cobble: '#C9C5D4',
} as const;

/** Side colour of a column, a touch deeper than its top, so cubes read as cubes. */
export const TILE_SIDE_COLORS = {
  grass: '#B6D3A0',
  dirt: '#D2B89C',
  sand: '#E3D2AC',
  water: '#A9CDEC',
  stone: '#C6C3CD',
  path: '#D9CCB8',
  crop: '#D6C98A',
  plank: '#D3AF7F',
  cobble: '#B4B0C2',
} as const;

export const PROP_COLORS = {
  trunk: '#C9A57E',
  trunkDark: '#A9835C',
  leaf: '#BFE0BB',
  leafAlt: '#D6E8B8',
  bush: '#C6E0B0',
  berry: '#FFB0C8',
  rock: '#CFCBD9',
  rockDark: '#B5B1C4',
} as const;

export const STRUCTURE_COLORS = {
  wall: '#F6EFE6',
  wallShade: '#EADFD1',
  wood: '#C9A57E',
  woodDark: '#A9835C',
  roofDefault: '#FFB9A3',
  stone: '#D9D6DE',
  leaf: '#BFE0BB',
  leafAlt: '#D6E8B8',
  crop: '#E9DE9E',
  cloth: '#FFD9E6',
  water: '#BEDDF6',
  ember: '#FF9E6B',
  sign: '#E8D3B0',
} as const;

/** Sky / light keyframes across the day. Interpolated on continuous hours. */
export interface SkyKey {
  h: number;
  sky: string;
  fog: string;
  sun: string;
  sunIntensity: number;
  hemiSky: string;
  hemiGround: string;
  hemiIntensity: number;
  ambient: number;
}

export const SKY_KEYS: SkyKey[] = [
  // deep night
  { h: 0, sky: '#232449', fog: '#2B2C55', sun: '#9FB0E8', sunIntensity: 0.22, hemiSky: '#3A3C6E', hemiGround: '#20213F', hemiIntensity: 0.5, ambient: 0.35 },
  { h: 4.5, sky: '#2E3060', fog: '#3A3B6B', sun: '#A8B6EA', sunIntensity: 0.25, hemiSky: '#454877', hemiGround: '#24254A', hemiIntensity: 0.55, ambient: 0.38 },
  // dawn, peach
  { h: 6.2, sky: '#FFD6C2', fog: '#FFD9CB', sun: '#FFC7A0', sunIntensity: 1.25, hemiSky: '#FFE3D4', hemiGround: '#E7C3B4', hemiIntensity: 0.62, ambient: 0.4 },
  // day, cream
  { h: 9, sky: '#E9F2FB', fog: '#F3EEE4', sun: '#FFF7E4', sunIntensity: 1.85, hemiSky: '#DCEBFB', hemiGround: '#E3D9C6', hemiIntensity: 0.6, ambient: 0.36 },
  { h: 15, sky: '#E4EFFB', fog: '#F2EBDF', sun: '#FFF3D9', sunIntensity: 1.8, hemiSky: '#D8E8FA', hemiGround: '#E0D5C1', hemiIntensity: 0.6, ambient: 0.36 },
  // dusk, lilac
  { h: 19, sky: '#E3D7FF', fog: '#D9CCF4', sun: '#FFBFA6', sunIntensity: 1.05, hemiSky: '#DCD0F7', hemiGround: '#B9AEDA', hemiIntensity: 0.55, ambient: 0.34 },
  // night, navy
  { h: 21.5, sky: '#2B2C55', fog: '#333462', sun: '#9FB0E8', sunIntensity: 0.25, hemiSky: '#3F4176', hemiGround: '#222341', hemiIntensity: 0.55, ambient: 0.38 },
  { h: 24, sky: '#232449', fog: '#2B2C55', sun: '#9FB0E8', sunIntensity: 0.22, hemiSky: '#3A3C6E', hemiGround: '#20213F', hemiIntensity: 0.5, ambient: 0.35 },
];

export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function mixHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** Lighten toward white (t > 0) or darken toward ink (t < 0). */
export function shade(hex: string, t: number): string {
  return t >= 0 ? mixHex(hex, '#FFFFFF', t) : mixHex(hex, '#2E2D3A', -t);
}

/** Smooth sky/light state for a continuous hour (0..24). */
export function skyAt(hourFloat: number): SkyKey {
  const h = ((hourFloat % 24) + 24) % 24;
  let a = SKY_KEYS[0];
  let b = SKY_KEYS[SKY_KEYS.length - 1];
  for (let i = 0; i < SKY_KEYS.length - 1; i++) {
    if (h >= SKY_KEYS[i].h && h <= SKY_KEYS[i + 1].h) {
      a = SKY_KEYS[i];
      b = SKY_KEYS[i + 1];
      break;
    }
  }
  const span = b.h - a.h || 1;
  const raw = (h - a.h) / span;
  // smoothstep so transitions ease in and out instead of ramping linearly
  const t = raw * raw * (3 - 2 * raw);
  return {
    h,
    sky: mixHex(a.sky, b.sky, t),
    fog: mixHex(a.fog, b.fog, t),
    sun: mixHex(a.sun, b.sun, t),
    sunIntensity: a.sunIntensity + (b.sunIntensity - a.sunIntensity) * t,
    hemiSky: mixHex(a.hemiSky, b.hemiSky, t),
    hemiGround: mixHex(a.hemiGround, b.hemiGround, t),
    hemiIntensity: a.hemiIntensity + (b.hemiIntensity - a.hemiIntensity) * t,
    ambient: a.ambient + (b.ambient - a.ambient) * t,
  };
}
